<template>
  <section class="checkout-confirmation-container vertical-spacer-4" aria-labelledby="order-confirmation">
    <pdl-heading id="order-confirmation" qaid="order-confirmation-header" class="mb-3 container" :level="1" size="xl">
      {{ $t('checkout.order.confirmation') }}
    </pdl-heading>
    <section class="container mb-6" aria-labelledby="order-confirmation-details">
      <pdl-heading id="order-confirmation-details" :level="2" size="lg">
        {{ $t('checkout.confirmation.thankyou') }}
      </pdl-heading>
      <div class="mb-3 flex flex-col" qaid="order-confirmation-code">
        <span>{{ $t('checkout.confirmation.orderid') }}&nbsp;{{ orderData.code }}</span>
        <pdl-label v-if="isRestrictedToRetailer" class="mt-1 w-auto" kind="prominent">
          {{ $t('checkout.B2B.trekU.orderStatus.message') }}
        </pdl-label>
      </div>
      <p class="mb-1">
        {{ $t('checkout.email.confirmation') }}&nbsp;<strong>{{ emailAddress }}</strong>
      </p>
      <p class="mb-3">{{ $t('checkout.confirmation.message') }}</p>
      <template v-if="calloutConfig">
        <pdl-callout :kind="calloutConfig.kind">
          <template #content>
            {{ calloutConfig.message }}
          </template>
        </pdl-callout>
      </template>
      <template v-if="hasUpdateRetailerOption">
        <p class="mb-1">{{ $t('checkout.confirmation.favourite.retailer') }}</p>
        <retailer-details
          class="address-and-billing-details__item text-strong"
          :retailer-for-delivery="orderData.retailerForDelivery"
          :has-title="false"
          qaid="update-retailer"
        />
        <div class="buttons">
          <pdl-button id="update-retailer-button" primary @click="updateRetailer">
            <span>{{ $t('checkout.confirmation.update.retailer') }}</span>
          </pdl-button>
          <pdl-link id="checkout-confirm-no-thanks-link" button secondary :href="`${encodedContextPath}`">
            <span>{{ $t('checkout.confirmation.no.thanks') }}</span>
          </pdl-link>
        </div>
      </template>
      <guest-register
        v-else-if="!isUserLoggedIn && isGuestRegisterFormVisible"
        :order-id="orderData.guid"
        :email-address="emailAddress"
        :has-electra-optin-toggle="hasElectraOptinToggle"
        @guestRegistered="handleGuestRegistration"
      />
    </section>
    <section class="grid md:grid-cols-2 lg:grid-cols-12 gap-x-8 lg:gap-x-6 row" aria-labelledby="delivery-summary">
      <pdl-heading
        id="delivery-summary"
        :level="2"
        size="md"
        class="mb-3 md:mb-5 columns lg:col-span-8 md:order-none lg:pr-2"
      >
        {{ $t('checkout.confirmation.delivery.summary') }}
      </pdl-heading>
      <section class="columns lg:col-span-8 md:order-none lg:pr-2" aria-labelledby="order-information">
        <pdl-heading id="order-information" :level="3" size="sm" class="mb-2">{{
          $t('checkout.summary.ordersummary')
        }}</pdl-heading>
        <div class="grid md:grid-cols-2 gap-x-8 gap-y-8 mb-4">
          <delivery-details
            :address="orderData.deliveryAddress"
            :email-address="emailAddress"
            qaid="delivery-summary"
          />
          <payment-method :payment-info="orderData.paymentInfo" is-showing-payment-method qaid="billing-details" />
        </div>
        <checkout-summary-list
          :cart-data="orderData"
          :cart-entries="cartData[0].entries"
          :retailer-for-delivery="orderData.retailerForDelivery"
          is-checkout-confirmation
        />
      </section>
      <aside class="columns lg:col-span-4 order-none" aria-labelledby="cart-summary">
        <order-summary-aside class="mb-4" hide-product-list-and-buttons :cart-data="orderData" />
        <section aria-labelledby="payment-methods">
          <payment-icon-list />
        </section>
      </aside>
    </section>
  </section>
</template>

<script>
import {mapMutations, mapState, mapGetters} from 'vuex';
import CheckoutSummaryList from '@/components/cart/b2c/CheckoutSummaryList.vue';
import GuestRegister from '@/components/checkout/GuestRegister.vue';
import {PdlHeading} from '@pedal/pdl-section-header';
import {PdlCallout} from '@pedal/pdl-callout';
import {PdlButton} from '@pedal/pdl-button';
import {PdlLink} from '@pedal/pdl-link';
import DeliveryDetails from '@/components/checkout/DeliveryDetails.vue';
import PaymentMethod from '@/components/checkout/PaymentMethod.vue';
import OrderSummaryAside from '@/components/checkout/order-summary-aside/OrderSummaryAside.vue';
import {UserApi} from '@/api/user.js';
import trackCheckout from '@/mixins/tracking/track-checkout.js';
import PaymentIconList from '@/components/PaymentIconList.vue';
import {PdlLabel} from '@pedal/pdl-label';
import RetailerDetails from '@/components/checkout/RetailerDetails.vue';

const GUEST_REGISTRATION_ERROR_CODE = 400;
const UPDATED_RETAILER_CALLOUT_KEY = 'hasUpdatedRetailer';

export default {
  name: 'CheckoutConfirmationContainer',
  components: {
    CheckoutSummaryList,
    GuestRegister,
    PdlHeading,
    DeliveryDetails,
    PaymentMethod,
    OrderSummaryAside,
    PdlCallout,
    PdlButton,
    PdlLink,
    PaymentIconList,
    PdlLabel,
    RetailerDetails,
  },

  mixins: [trackCheckout],

  props: {
    cartData: {
      type: Array,
      default: () => null,
    },
    orderData: {
      type: Object,
      default: () => ({}),
    },
    emailAddress: {
      type: String,
      default: '',
    },
    isClickCollect: {
      type: Boolean,
      default: false,
    },
    hasElectraOptinToggle: {
      type: Boolean,
      default: false,
    },
    hasChangedRetailer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hasUpdatedRetailer: false,
      isGuestRegisterFormVisible: true,
      calloutConfig: null,
    };
  },
  computed: {
    ...mapState('backend', ['encodedContextPath', 'isUserLoggedIn']),
    ...mapGetters('checkout', ['isRestrictedToRetailer']),

    hasUpdateRetailerOption() {
      return this.isUserLoggedIn && this.hasChangedRetailer && !this.hasUpdatedRetailer;
    },
    checkoutConfirmationCalloutConfigMap() {
      return {
        200: {
          kind: 'success',
          message: this.$t('account.create.confirm'),
        },
        400: {
          kind: 'error',
          message: this.$t('errorMessage.unableToProcess'),
        },
        406: {
          kind: 'error',
          message: this.$t('registration.error.account.exists.title'),
        },
        409: {
          kind: 'error',
          message: this.$t('account.verify.unverified.email'),
        },
        hasUpdatedRetailer: {
          kind: 'success',
          message: this.$t('appleLabel.changesSaved'),
        },
      };
    },
  },
  created() {
    // Sets the entries to the checkout store for Multi steps
    this.setupCartData();
  },
  methods: {
    ...mapMutations('checkout', ['setB2cCartData']),

    handleGuestRegistration(responseCode) {
      this.calloutConfig = this.checkoutConfirmationCalloutConfigMap[responseCode];
      if (responseCode !== GUEST_REGISTRATION_ERROR_CODE) {
        this.isGuestRegisterFormVisible = false;
      }
    },

    /**
     * Handles setting the entries into the checkout store.
     */
    setupCartData() {
      if (this.cartData === null) return;

      this.setB2cCartData(this.cartData);
    },

    updateRetailer() {
      UserApi.updatePreferredRetailer(this.orderData.retailerForDelivery.name, this.orderData.code)
        .then(() => {
          this.calloutConfig = this.checkoutConfirmationCalloutConfigMap[UPDATED_RETAILER_CALLOUT_KEY];
          this.hasUpdatedRetailer = true;
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.checkout-confirmation-container {
  margin-bottom: 7.5rem;
}

.address-and-billing-details__container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
</style>
